import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    CircularProgress,
    IconButton,
    InputAdornment,
    Link,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { assetService, cacheService, orderService, searchService } from "../../../../../Core/Api/services";
import { produce } from "immer";
import { ObjectLayer } from "../../../../../Core/Api/Types/object-layer";
import { BaseMetadataType } from "../../../../../Core/Api/Types/base-metadata-type";
import { IDbBaseMetadataValueInfos } from "../../../../../Core/Api/Types/i-db-base-metadata-value-infos";
import MovieSlider from "../../../../../Components/Sliders/MovieSlider";
import { MovieCardProps } from "../../../../../Components/Cards/MovieCard/movie-card";
import { GetTitleRouterPath, mapTitleObjectToMovieCard } from "../../../../../Utils/helpers";
import { SearchRequestNative } from "../../../../../Core/Api/Types/search-request-native";
import { ComputeValueBaseTypeFromMetaBaseType } from "../../../../../Core/Utils/functions";
import { TitleStatus } from "../../../../../Core/Api/Types/title-status";
import ListPageHeader, {
    ListPageHeaderGroupActions,
    ListPageHeaderRight,
} from "../../../../../Components/Misc/ListPageHeader";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import FlagIcon from "@mui/icons-material/Flag";
import GlobalConst from "../../../../../Core/Utils/Constants/globals";
import { GetBroadcasterList, SetBroadcasters } from "../../../../../Redux/Reducers/redux-slice-bundle-maker";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ToggleLayout from "../../../../../Components/Misc/ToggleLayout";
import { DbAssetObjectInfos } from "../../../../../Core/Api/Types/db-asset-object-infos";
import { GridColDef } from "@mui/x-data-grid";
import TableCellMovie from "../../../../../Components/Table/TableCellMovie/table-cell-movie";
import { GetMetadataByTagName } from "../../../../../Core/Utils/asset-object-utils";
import { Link as RouterLink } from "react-router";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import {
    getBroacasterIdMetadata,
    getBroadcasterImageSrc,
    getBroadcasterName,
    getBundlesContent,
} from "../../../../../Redux/Utils/Bundlemaker/bundlemaker-utils";
import { ObjectClass } from "../../../../../Core/Api/Types/object-class";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../../Components/Table/table-uuids";

type TitlePlatformCellProps = {
    titleId: number;
};
const TitlePlatformCell: React.FC<TitlePlatformCellProps> = (props) => {
    const { t } = useTranslation();
    const { titleId } = props;
    const [loading, setLoading] = useState(true);
    const [packagePlatforms, setPackagesPlatforms] = useState<{ name: string; src: string }[]>([]);
    const broadcasters = useSelector(GetBroadcasterList);
    useEffect(() => {
        if (!broadcasters || broadcasters.length === 0) return;

        assetService.GetObjectLightById(titleId, 2).then((result) => {
            if (result.graph) {
                const bundles = getBundlesContent(result.graph);
                const packageList = bundles?.childObjects?.filter(
                    (x) => x.classification === ObjectClass.Project && !x.isDeleted
                );

                if (packageList && packageList.length > 0) {
                    const broadcastersInfos: any[] = [];
                    for (let i = 0; i < packageList.length; i++) {
                        const broadcasterId = getBroacasterIdMetadata(packageList[i]);
                        const brName = getBroadcasterName(broadcasterId, broadcasters);
                        if (broadcasterId && brName && !broadcastersInfos.find((x) => x.name === brName))
                            broadcastersInfos.push({ name: brName, src: getBroadcasterImageSrc(broadcasterId, broadcasters) });
                    }
                    setPackagesPlatforms(broadcastersInfos);
                }
                setLoading(false);
            }
        });
    }, [titleId, broadcasters.length]);

    if (loading)
        return (
            <Box
                position={"relative"}
                width={"100%"}
                height={"100%"}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
            >
                <CircularProgress size={20} />
            </Box>
        );

    if (packagePlatforms.length === 0) return <Typography color={"text.tier"}>{t("page:body.noPackages")}</Typography>;

    return (
        <Stack spacing={0.5} direction={"row"}>
            {packagePlatforms.map((x) => (
                <img src={x.src} alt={x.name} width={24} height={24} loading="lazy" />
            ))}
        </Stack>
    );
};

interface statusTitleInfos {
    status: TitleStatus;
    items: Omit<MovieCardProps, "index">[];
}

function InventoryBundlesDashboard() {
    const { t } = useTranslation();
    const nbTitleByStatus = 100;
    const dispatch = useDispatch();
    const metadataFields = cacheService.GetMetadata();
    const broadcasters = useSelector(GetBroadcasterList);
    const [isLoadingNewIn, setIsLoadingNewIn] = React.useState<boolean>(true);
    const [titlesLatestCatalog, setTitlesLatestCatalog] = React.useState<Omit<MovieCardProps, "index">[]>([]);
    const [titleObjects, setTitleObjects] = React.useState<DbAssetObjectInfos[]>([]);

    // Filter values
    const [providers, setProviders] = React.useState<any[]>([]);
    const [labs, setLabs] = React.useState<any[]>([]);
    const [rightHolders, setRightHolders] = React.useState<any[]>([]);

    // Filters
    const [filterProvider, setFilterProvider] = React.useState<string>("");
    const [filterRightHolder, setFilterRightHolder] = React.useState<string>("");
    const [filterLab, setFilterLab] = React.useState<string>("");
    const [filterStatus, setFilterStatus] = React.useState<string>("");

    const [filterText, setFilterText] = React.useState<string>("");
    const [layout, setLayout] = React.useState<"grid" | "list">("grid");

    const [infos, setInfos] = React.useState<statusTitleInfos[]>(
        Object.keys(TitleStatus).map((key) => ({
            status: TitleStatus[key as keyof typeof TitleStatus],
            items: [],
        }))
    );

    //Tagnames
    const metaProviderTagName = "vodProvider";
    const metaRightHolderTagName = "vodRightHolder";
    const metaLabTagName = "vodLab";
    const metaTitleStatusTagName = "bundleTitleStatus";

    const onLayoutChange = (e: React.MouseEvent<HTMLElement>, value: "grid" | "list") => {
        setLayout(value);
    };

    const metadataTitleTypeId = metadataFields.find(
        (m) => m.tagName === GlobalConst.TITLE_STATUS_TAGNAME && m.layer === ObjectLayer.Title
    )?.id;

    function createFilters(value) {
        const filters: IDbBaseMetadataValueInfos[] = [
            {
                $type: ComputeValueBaseTypeFromMetaBaseType(BaseMetadataType.DbStringMetadataValueInfos),
                id: metadataTitleTypeId!,
                fieldType: BaseMetadataType.DbStringMetadataValueInfos,
                value: value,
                creationTime: new Date().toISOString(),
                updateTime: null,
                fieldName: null,
                fieldLabel: null,
                targetLayer: ObjectLayer.Title,
                valueLabel: null,
            },
        ];
        return filters;
    }

    useEffect(() => {
        (!broadcasters || broadcasters.length === 0) &&
            orderService.GetBroadcastersBundlemakerList().then((response) => {
                if (response && response.broadcasters) {
                    dispatch(SetBroadcasters(response.broadcasters));
                }
            });
        setTitleObjects([]);
        const fetchTitles = async () => {
            try {
                const latestTitlesPromise = assetService
                    .ListLastObjects(ObjectLayer.Title, nbTitleByStatus)
                    .then((response) => {
                        if (response.results) {
                            setTitleObjects((prevTitleObjects) => [...prevTitleObjects, ...response.results!]);
                            const movieCardsDataCatalog = response.results
                                .reverse()
                                .map((title) => mapTitleObjectToMovieCard(title, metadataFields));
                            setTitlesLatestCatalog(movieCardsDataCatalog);
                        }
                    });

                const statusPromises = Object.keys(TitleStatus).map((status) => {
                    return searchService
                        .SearchBasic(
                            new SearchRequestNative({
                                outputLayer: ObjectLayer.Title,
                                hideTitleWithoutAsset: false,
                                onlyConfidential: false,
                                pattern: "*",
                                metadata: createFilters(status),
                                titleCustomerIds: null,
                                limit: nbTitleByStatus,
                            })
                        )
                        .then((response) => {
                            if (response.results) {
                                setTitleObjects((prevTitleObjects) => [...prevTitleObjects, ...response.results!]);
                                const movieCards = response.results
                                    .reverse()
                                    .map((title) => mapTitleObjectToMovieCard(title, metadataFields));

                                setInfos(
                                    produce((draft) => {
                                        const index = draft.findIndex((x) => x.status === status);
                                        if (index !== -1) {
                                            draft[index].items = movieCards;
                                        }
                                    })
                                );
                            }
                        });
                });

                await Promise.all([latestTitlesPromise, ...statusPromises]);

                setIsLoadingNewIn(false);
            } catch (error) {
                console.error("Error fetching titles:", error);
            }
        };

        fetchTitles();
        setProviders(metadataFields.find((x) => x.tagName === metaProviderTagName)?.values!);
        setRightHolders(metadataFields.find((x) => x.tagName === metaRightHolderTagName)?.values!);
        setLabs(metadataFields.find((x) => x.tagName === metaLabTagName)?.values!);

        if (!broadcasters || broadcasters.length === 0)
            orderService.GetBroadcastersBundlemakerList().then((response) => {
                if (response && response.broadcasters) {
                    dispatch(SetBroadcasters(response.broadcasters));
                }
            });
    }, []);

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "id",
                headerName: "Id",
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                renderCell: (data: any) => (
                    <Link
                        color="primary"
                        underline="hover"
                        component={RouterLink}
                        target="_blank"
                        to={`${GetTitleRouterPath()}/${data?.row?.id}/bundlemaker`}
                    >
                        {data.value}
                    </Link>
                ),
            },
            {
                field: "status",
                headerName: t("common:status"),
                valueGetter: (data: any, row: any) => GetMetadataByTagName(row, metaTitleStatusTagName)?.value ?? "-",
                renderCell: (data: any) => (
                    <Typography variant="body1" color="text.secondary">
                        {data.value}
                    </Typography>
                ),
                flex: CellSize.medium,
                minWidth: CellSize.medium,
            },
            {
                field: "name",
                headerName: t("common:title"),
                flex: CellSize.xxlarge,
                minWidth: CellSize.xxlarge,
                renderCell: (data: any) => <TableCellMovie id={data.id} name={data.value} />,
            },
            {
                field: "provider",
                headerName: t("common:provider"),
                valueGetter: (data: any, row: any) => GetMetadataByTagName(row, metaProviderTagName)?.value ?? "-",
                renderCell: (data: any) => (
                    <Typography variant="body1" color="text.secondary">
                        {data.value}
                    </Typography>
                ),
                flex: CellSize.medium,
                minWidth: CellSize.medium,
            },
            {
                field: "rightHolder",
                headerName: t("common:rightHolder"),
                valueGetter: (data: any, row: any) => GetMetadataByTagName(row, metaRightHolderTagName)?.value ?? "-",
                renderCell: (data: any) => (
                    <Typography variant="body1" color="text.secondary">
                        {data.value}
                    </Typography>
                ),
                flex: CellSize.medium,
                minWidth: CellSize.medium,
            },
            {
                field: "lab",
                headerName: t("common:lab"),
                valueGetter: (data: any, row: any) => GetMetadataByTagName(row, metaLabTagName)?.value ?? "-",
                renderCell: (data: any) => (
                    <Typography variant="body1" color="text.secondary">
                        {data.value}
                    </Typography>
                ),
                flex: CellSize.medium,
                minWidth: CellSize.medium,
            },
            {
                field: "platforms",
                headerName: t("table:headerName.platforms"),
                sortable: false,
                flex: CellSize.medium,
                minWidth: CellSize.medium,
                renderCell: (data: any) => <TitlePlatformCell titleId={data.id} />,
            },
        ],
        []
    );

    const filteredRows = useMemo(() => {
        if (layout !== "list") return [];

        return titleObjects
            .filter((obj, index, self) => index === self.findIndex((o) => o.id === obj.id))
            .filter(
                (x) =>
                    (!filterText || x.name.includesAllWords(filterText)) &&
                    (filterProvider === "" || GetMetadataByTagName(x, metaProviderTagName)?.value === filterProvider) &&
                    (filterRightHolder === "" || GetMetadataByTagName(x, metaRightHolderTagName)?.value === filterRightHolder) &&
                    (filterLab === "" || GetMetadataByTagName(x, metaLabTagName)?.value === filterLab) &&
                    (filterStatus === "" || GetMetadataByTagName(x, metaTitleStatusTagName)?.value === filterStatus)
            );
    }, [filterText, titleObjects, filterProvider, filterRightHolder, filterLab, filterStatus, layout]);

    const filteredItems = (items: Omit<MovieCardProps, "index">[]) => {
        if (layout !== "grid") return [];

        return items
            .filter((obj, index, self) => index === self.findIndex((o) => o.id === obj.id))
            .filter(
                (x) =>
                    (!filterText || x.title.includesAllWords(filterText)) &&
                    (filterProvider === "" ||
                        GetMetadataByTagName(
                            titleObjects.find((y) => y.id === x.id),
                            metaProviderTagName
                        )?.value === filterProvider) &&
                    (filterRightHolder === "" ||
                        GetMetadataByTagName(
                            titleObjects.find((y) => y.id === x.id),
                            metaRightHolderTagName
                        )?.value === filterRightHolder) &&
                    (filterLab === "" ||
                        GetMetadataByTagName(
                            titleObjects.find((y) => y.id === x.id),
                            metaLabTagName
                        )?.value === filterLab) &&
                    (filterStatus === "" ||
                        GetMetadataByTagName(
                            titleObjects.find((y) => y.id === x.id),
                            metaTitleStatusTagName
                        )?.value === filterStatus)
            );
    };

    const additionalFilters = (
        <>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                startAdornment={<ManageAccountsOutlinedIcon />}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={filterProvider}
                onChange={(e) => setFilterProvider(e.target.value)}
            >
                <MenuItem value="" selected>
                    {t("common:provider")}
                </MenuItem>
                {providers.map((provider, index) => (
                    <MenuItem value={provider.value} key={index}>
                        {provider.value}
                    </MenuItem>
                ))}
            </Select>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                startAdornment={<PersonOutlineOutlinedIcon />}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={filterRightHolder}
                onChange={(e) => setFilterRightHolder(e.target.value)}
            >
                <MenuItem value="" selected>
                    {t("common:rightHolderUpperCase")}
                </MenuItem>
                {rightHolders.map((rh, index) => (
                    <MenuItem value={rh.value} key={index}>
                        {rh.value}
                    </MenuItem>
                ))}
            </Select>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                startAdornment={<GroupWorkIcon />}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={filterLab}
                onChange={(e) => setFilterLab(e.target.value)}
            >
                <MenuItem value="" selected>
                    {t("common:lab")}
                </MenuItem>
                {labs.map((lab, index) => (
                    <MenuItem value={lab.value} key={index}>
                        {lab.value}
                    </MenuItem>
                ))}
            </Select>
            <Select
                displayEmpty
                IconComponent={ExpandMoreRoundedIcon}
                startAdornment={<FlagIcon />}
                inputProps={{ "aria-label": "Without label" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
            >
                <MenuItem value="" selected>
                    {t("common:status")}
                </MenuItem>
                {Object.keys(TitleStatus).map((status) => (
                    <MenuItem value={status} key={status}>
                        {status}
                    </MenuItem>
                ))}
            </Select>
        </>
    )

    const additionalActions = (
        <ToggleLayout value={layout} onChange={onLayoutChange} />
    )

    return (
        <>
            {layout === "grid" ? (
                <Stack>
                    <Stack direction="row" py={2} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
                        <Stack direction="row" gap={1} flex={"1 1 0%"}>
                            <OutlinedInput
                                label={t("common:search")}
                                placeholder={t("field:placeholder.filter")}
                                value={filterText}
                                onChange={(value) => setFilterText(value.target.value.toLowerCase())}
                                endAdornment={
                                    <InputAdornment position="end">
                                        {filterText && (
                                            <IconButton aria-label="Clear field" onClick={() => setFilterText("")} size="small" sx={{ mr: 1 }}>
                                                <CloseIcon />
                                            </IconButton>
                                        )}
                                        <SearchIcon />
                                    </InputAdornment>
                                }
                            />
                            {additionalFilters}
                        </Stack>
                        {additionalActions}
                    </Stack>
                    <Stack gap={2} pt={2}>
                        {filteredItems(titlesLatestCatalog).length > 0 && filterStatus === "" && (
                            <Box component="section">
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                    <Typography component="h2" variant="h4">
                                        {t("page:body.lastCreatedTitles")}
                                    </Typography>
                                </Stack>
                                <MovieSlider
                                    list={filteredItems(titlesLatestCatalog)}
                                    loading={isLoadingNewIn}
                                    nbLoadingItems={10}
                                    bundleMaker={true}
                                />
                            </Box>
                        )}

                        {infos.map((info) => (
                            <>
                                {info.items.length > 0 && filteredItems(info.items).length > 0 && (
                                    <Stack>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                            <Typography component="h2" variant="h4">
                                                {t("page:body.lastStatusTitles", { status: info.status })}
                                            </Typography>
                                        </Stack>
                                        <MovieSlider
                                            list={filteredItems(info.items)}
                                            loading={isLoadingNewIn}
                                            nbLoadingItems={10}
                                            bundleMaker={true}
                                        />
                                    </Stack>
                                )}
                            </>
                        ))}
                    </Stack>
                </Stack>
            ) : (
                <>
                    <DataTable
                        tableKey={TableUuids.INVENTORY_BUNDLES}
                        additionalActions={additionalActions}
                        additionalFilters={additionalFilters}
                        rows={filteredRows}
                        columns={columns}
                        getRowHeight={() => "auto"}
                        columnHeaderHeight={24}
                        checkboxSelection={false}
                        disableRowSelectionOnClick
                        pagination={true}
                    />
                </>
            )}
        </>
    );
}

export default InventoryBundlesDashboard;
